<script setup lang="ts">
  import { computed } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import { useCajaSelection } from '@/composition/use-caja-selection';
  import { usePosMoney } from '@/composition/use-pos-money';
  import type { IReservation } from '@/interfaces/IReservation';
  import { useOrdersStore } from '@/stores/orders.store';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  const props = defineProps<{
    reservation: IReservation;
    busy: boolean;
    canOrders: boolean;
  }>();
  const emit = defineEmits<{
    (event: 'apply'): void;
    (event: 'refund'): void;
  }>();
  const restaurant = useRestaurantStore();
  const orders = useOrdersStore();
  const money = usePosMoney();
  const { selectCaja } = useCajaSelection();
  const unappliedPaid = computed(
    () =>
      props.reservation.depositStatus === 'paid' &&
      !props.reservation.depositAppliedAt
  );
  const hasDepositToApply = computed(
    () => props.reservation.status === 'seated' && unappliedPaid.value
  );
  // A visit that ended (or an absence) with money still held: staff can give it back.
  const hasDepositToRefund = computed(
    () =>
      unappliedPaid.value &&
      (props.reservation.status === 'finished' ||
        props.reservation.status === 'no_show')
  );
  const sessionHasOrders = computed(
    () =>
      !!props.reservation.tableSessionId &&
      !!orders.orders?.some(
        (order) => order.tableSessionId === props.reservation.tableSessionId
      )
  );
  const applyDisabled = computed(
    () =>
      !props.canOrders ||
      !sessionHasOrders.value ||
      (!!restaurant.selectedBranch?.settingUseCajas &&
        (!restaurant.caja || restaurant.caja.status !== 'open'))
  );
  const cajas = computed(
    () =>
      restaurant.cajas?.filter(
        (caja) =>
          caja.enabled && caja.branchId === restaurant.selectedBranch?.id
      ) ?? []
  );
  function chooseCaja(event: Event) {
    if (!(event.target instanceof HTMLSelectElement)) return;
    const id = Number(event.target.value);
    const caja = cajas.value.find((value) => value.id === id);
    if (caja && caja.id !== restaurant.caja?.id) selectCaja(caja);
  }
</script>
<template>
  <section
    v-if="
      hasDepositToApply ||
      hasDepositToRefund ||
      (reservation.depositUnappliedAmount || 0) > 0
    "
    class="reservation-deposit-card"
  >
    <p v-if="hasDepositToApply || hasDepositToRefund" class="deposit-value">
      {{
        $t('reservations.pos.depositPaid', {
          amount: money.formatComputedMoney(reservation.depositAmount || 0),
        })
      }}
    </p>
    <PosUiButton
      v-if="hasDepositToRefund"
      class="refund-action"
      color="neutral"
      variant="outline"
      :disabled="busy"
      @click="emit('refund')"
      >{{ $t('reservations.deposit.refundPaid') }}</PosUiButton
    >
    <p v-if="(reservation.depositUnappliedAmount || 0) > 0" class="muted">
      {{
        $t('reservations.deposit.leftover', {
          amount: money.formatComputedMoney(
            reservation.depositUnappliedAmount || 0
          ),
        })
      }}
    </p>
    <template v-if="hasDepositToApply && canOrders"
      ><label
        v-if="restaurant.selectedBranch?.settingUseCajas"
        class="caja-field"
        ><span>{{ $t('posV2.cajaLabel') }}</span
        ><select
          class="custom-input"
          :value="restaurant.caja?.id || ''"
          :disabled="busy"
          @change="chooseCaja"
        >
          <option value="" disabled>
            {{ $t('cajaV2.noCajaTitle') }}
          </option>
          <option v-for="caja in cajas" :key="caja.id" :value="caja.id">
            {{ caja.name }}
          </option>
        </select></label
      ><PosUiButton
        color="success"
        variant="soft"
        :disabled="busy || applyDisabled"
        @click="emit('apply')"
        >{{ $t('reservations.pos.applyDeposit') }}</PosUiButton
      >
      <p v-if="canOrders && !sessionHasOrders" class="section-hint">
        {{ $t('reservations.pos.applyDepositNoOrders') }}
      </p></template
    >
  </section>
</template>
<style scoped lang="scss">
  .reservation-deposit-card {
    @apply tw-border-b tw-border-neutral-200 tw-px-5 tw-py-4;
    h3 {
      @apply tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-neutral-500;
    }
    @apply tw-flex tw-flex-col tw-gap-3;
    .section-hint {
      @apply tw-mt-1 tw-text-xs tw-text-neutral-400;
    }
    .muted {
      @apply tw-text-sm tw-text-neutral-500;
    }
    .deposit-value {
      @apply tw-font-semibold tw-tabular-nums tw-text-emerald-700;
    }
    .refund-action {
      @apply tw-self-start;
    }
    .caja-field {
      @apply tw-flex tw-flex-col tw-gap-1 tw-text-xs tw-text-neutral-500;
    }
    .custom-input {
      @apply tw-min-h-[44px] tw-w-full tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-white tw-px-3 tw-text-sm tw-text-neutral-900;
    }
  }
</style>
